import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnInit,
} from '@angular/core';
import { ModalData, ModalRef } from '@purespectrum1/ui/modal';
import { BulkOptionConfirmationModalData } from './bulk-option.interface';
import {
  HighCpiConfrmationResponse,
  McSVPEachSurvey,
  ProjectResponse,
  SurveyListing,
} from '../../shared/services/buyer-api/survey.interface';
import {
  BehaviorSubject,
  Observable,
  Subject,
  Subscription,
  from,
  iif,
  merge,
  of,
  partition,
} from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  share,
  switchMap,
  tap,
  toArray,
  withLatestFrom,
} from 'rxjs/operators';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import { ProjectManagers } from '../../operator/user-service/user.interface';
import { BulkView, BulkViewColumn } from '../bulk-edit/domain/types';
import { BuyerApiService } from '../../shared/services/buyer-api/survey.service';
import { notifyMessage } from '../../constants/notify-message';
import { SurveyStatus } from '../../utils/survey-status';
import {
  BulkDeleteSurveyRequestConfig,
  BulkEditBillingNumberRequest,
  BulkGroupSurveysRequestConfig,
  BulkPatchSurveyRequestConfig,
  BulkRequestConfig,
} from '../bulk-edit/domain/bulk-operation-request-formatter';
import { BillingNumberRules } from '@purespectrum1/ui/marketplace/shared/interfaces/billing-rules.interfaces';
import { BillingRulesService } from '../bulk-edit/bulk-edit-container/bulk-edit-container.service';
import { AuthService } from '../../auth/auth.service';
import {
  BulkEditPayloadValue,
  EditableSurvey,
  EditableSurveyEdited,
  EditableSurveyError,
  SurveyModePending,
} from '../bulk-edit/types';
import { LayoutConstants } from '../../layout/layout-constants';
import {
  ComposedState,
  EditableSurveyStateComposer,
} from './domain/editable-survey-state-composer';
import { BulkEditStatusMapper } from '../bulk-edit/domain/bulk-edit-status-mapper';
import { INVOICE_TYPE } from '../../constants/invoice-type';
import {
  BulkViewBillingActionColumn,
  BulkViewCostColumn,
  BulkViewCurrencyColumn,
  BulkViewHighCpiColumn,
  BulkViewInfoColumn,
  BulkViewStatusColumn,
} from '../bulk-edit/domain/bulk-view-columns';
import { SURVEY_STATUS } from '../../constants/survey-status';
import { Constants } from '../../create-surveys/create-survey.constants';
import { Router } from '@angular/router';
import { SurveyRouterLinkPipe } from '../pipes/survey-router-link.pipe';
import { EachCountry } from '../../shared/interfaces/create-surveys.interface';
import {
  ActivityLogsService,
  HighCpiConfirmationActivityGenerator,
  PROJECT_TYPES,
} from '@purespectrum1/ui';
import { ErrorMessageFormatterService } from './domain/error-message-formatter.service';
import { ChildTrackerStats } from '@purespectrum1/ui/marketplace/shared/interfaces/survey.interface';

@Component({
  selector: 'ps-bulk-option-confirmation',
  templateUrl: './bulk-option-confirmation.component.html',
  styleUrls: ['./bulk-option-confirmation.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BulkOptionConfirmationComponent implements OnInit {
  public data: BulkOptionConfirmationModalData = this._modalData;

  public surveyIds: Pick<SurveyListing, 'surveyId'>[] = [];
  public surveys$!: Observable<EditableSurvey[]>;
  public managers: ProjectManagers[] = [];
  public view!: BulkView;
  public columns$!: Observable<BulkViewColumn[]>;
  public showError: boolean = false;
  public disableButton: boolean = false;
  public billingRules$!: Observable<BillingNumberRules>;
  public MAX_CPI_THRESHOLD = LayoutConstants.MAX_CPI_THRESHOLD;
  public AVERAGE_MARGIN = Constants.AVERAGE_VARIABLE_MARGIN;
  public isCpiConfirmed: boolean = false;
  public MAX_CPI_THRESHOLD_SURPASSED_ERROR =
    notifyMessage.CPI_CONFIRMATION_ERROR;
  public readonly SURVEY_STATUS = SURVEY_STATUS;
  public surveyStatus: Array<[string, number]> = [];
  public showBillingInputForEdit = false;

  private _surveysCommand$ = new BehaviorSubject<EditableSurvey[]>([]);
  private _updateCommand$ = new Subject<EditableSurvey[]>();
  private _feedbackCommand$ = new Subject<EditableSurvey>();
  private _deleteCommand$ = new Subject<EditableSurvey>();
  private _closeCommand$ = new Subject();
  private _highCpiConfirmationActivity$ = new Subject<EditableSurvey>();

  private _subscription: Subscription = new Subscription();
  private _surveys: EditableSurvey[] = [];
  PROJECT_TYPES = PROJECT_TYPES;

  public get value() {
    return this._modalData.value;
  }

  public get surveys() {
    return this._modalData.surveys;
  }
  public countries: EachCountry[] = this.data.countries;
  constructor(
    @Inject(ModalData)
    private _modalData: BulkOptionConfirmationModalData,
    private _modalRef: ModalRef<BulkOptionConfirmationComponent, boolean>,
    private _buyerApiService: BuyerApiService,
    private _toast: ToasterService,
    private _billingRulesService: BillingRulesService,
    private _auth: AuthService,
    private readonly _route: Router,
    private readonly _activityLogsService: ActivityLogsService,
    private readonly _errorMessageFormatterService: ErrorMessageFormatterService
  ) {}

  public ngOnInit() {
    this.view = this._modalData.view;
    this.managers = this._modalData.managers;

    const filteredStatus = new BulkEditStatusMapper(
      this._auth.companyConfig.generateInvoice || INVOICE_TYPE.MONTHLY
    ).list();
    this.surveyStatus = Object.entries(filteredStatus);

    const initial: EditableSurvey[] = this._modalData.surveys.map((survey) => {
      const found = (this._modalData.highCpiDetails ?? []).find(
        (item) => item.survey_id === survey.surveyId
      );
      const highCpiDetails = found?.details || <HighCpiConfrmationResponse>{};
      const state = new EditableSurveyStateComposer(
        survey,
        this.value,
        highCpiDetails
      );
      return this.view.operation() === 'Edit STATUS' &&
        this.value == SURVEY_STATUS.LIVE.toString() &&
        highCpiDetails.isPriceGreaterThanCpiThreshold
        ? state.error(
            notifyMessage.CPI_CONFIRMATION_ERROR_ON_STATUS_CHANGE,
            'toaster'
          )
        : state.pending();
    });

    const feedback$ = this._feedbackCommand$.pipe(
      map((feedback) => {
        return this._surveys.map((survey) => {
          if (survey.id === feedback.id) {
            return feedback;
          }
          return survey;
        });
      })
    );

    const delete$ = this._deleteCommand$.pipe(
      map(({ id }) => this._surveys.filter((survey) => survey.id !== id))
    );

    const update$ = this._updateCommand$.pipe(
      switchMap(() => this._validations())
    );

    const [updateWithError$, updateWithSuccess$] = partition(
      update$,
      (surveys) => this._isSurveysHaveError(surveys)
    );

    // Handle updates with errors
    const errorStream$ = updateWithError$.pipe(
      tap(this._handleValidationErrors)
    );

    // Handle updates with success
    const successStream$ = updateWithSuccess$.pipe(
      switchMap((surveys) => {
        if (this.view.operationType() === 'each') {
          return this._opearateIndividually(surveys);
        }

        return this._operateCollectively(surveys);
      })
    );

    // Merge the two streams
    const finalUpdate$ = merge(errorStream$, successStream$).pipe(
      filter((surveys) => !this._isSurveysHaveError(surveys, true)),
      share()
    );

    finalUpdate$.subscribe({
      next: (surveys) => {
        const error = surveys.some((survey) => survey.status === 'error');
        if (!error) {
          this._toast.success(
            notifyMessage.successMessage.BULK_EDIT.OPERATION_COMPLETED.replace(
              '<OPERATION_TYPE>',
              this._modalData.view.operation()
            )
          );
        } else {
          this._toast.error(
            notifyMessage.successMessage.BULK_EDIT.OPERATION_FAILED
          );
        }
      },
    });

    this.surveys$ = merge(this._surveysCommand$, feedback$, delete$).pipe(
      switchMap((editable) =>
        from(editable).pipe(
          filter((survey) =>
            ['pending', 'error', 'warning'].includes(survey.status)
          ),
          toArray()
        )
      ),
      tap((surveys) => {
        this._surveys = surveys;
      })
    );

    const close$ = this._closeCommand$.pipe(
      withLatestFrom(this.surveys$),
      map(([_, surveys]) => surveys)
    );

    merge(close$, finalUpdate$)
      .pipe(
        map((surveys) => surveys.some((survey) => survey.status === 'edited'))
      )
      .subscribe((close) => this._modalRef.close(close));
    this._fetchBillingNumberRules();
    this._surveysCommand$.next(initial);
    this._subscription.add(this._closeCommand$);

    this.columns$ = this.surveys$.pipe(
      switchMap((surveys) => this._handleDynamicColumns(surveys)),
      map(() => this.view.columns())
    );

    this._handleHighCpiConfirmationActivity();
  }

  private _handleHighCpiConfirmationActivity() {
    const hightCpiConfirmation$ = this._highCpiConfirmationActivity$.pipe(
      filter(() => ['Edit STATUS', 'Edit CPI'].includes(this.view.operation())),
      switchMap(
        ({ survey: { surveyId }, highCpiDetails, value }: EditableSurvey) => {
          const cpi =
            this.view.operation() === 'Edit CPI'
              ? value
              : highCpiDetails?.pricingDetails.value;
          const activityPayload = new HighCpiConfirmationActivityGenerator(
            surveyId,
            cpi!.toString()
          ).generate();
          return this._activityLogsService.post(activityPayload);
        }
      )
    );

    this._subscription.add(hightCpiConfirmation$.subscribe());
  }

  private _handleValidationErrors = (surveys: EditableSurvey[]): void => {
    surveys.forEach((survey) => {
      if (this._isSurveyInErrorState(survey, false)) {
        if ('error' in survey && survey.status === 'error') {
          this._toast.error(survey.error as string);
        }
      }

      if (Array.isArray(survey.survey.mc)) {
        survey.survey.mc.forEach((childSurvey) => {
          if (childSurvey.status === 'error') {
            this._toast.error(childSurvey.message.ps_api_response_message);
          }
        });
      }
    });
  };

  private _isSurveysHaveError(
    surveys: EditableSurvey[],
    completeErrorCheck: boolean = false
  ): boolean {
    return surveys.some((survey) =>
      this._isSurveyInErrorState(survey, completeErrorCheck)
    );
  }

  private _isSurveyInErrorState(
    survey: EditableSurvey,
    completeErrorCheck: boolean
  ): boolean {
    const hasErrorOrWarning =
      survey.status === 'error' ||
      (survey.status === 'warning' && !!survey.isBelowAverageMargin);
    const validPositions = completeErrorCheck
      ? ['toaster', 'info-column']
      : ['toaster'];

    if (
      hasErrorOrWarning &&
      'position' in survey &&
      validPositions.includes(survey.position)
    ) {
      return true;
    }

    return false;
  }

  private _validations(): Observable<EditableSurvey[]> {
    this._surveys.forEach((item) => {
      if (item.status === 'pending') {
        const state = new EditableSurveyStateComposer(
          item.survey,
          item.value,
          item.highCpiDetails
        );
        const updatedSurvey = this.view.validate(state, 'submission');
        this._feedbackCommand$.next(updatedSurvey);
      }
    });
    return of(this._surveys);
  }

  public onDeleteSurvey(survey: EditableSurvey) {
    this._deleteCommand$.next(survey);
  }

  public onClose() {
    this._closeCommand$.next();
  }

  public onConfirm() {
    this._updateCommand$.next(this._surveys);
  }

  public displayStatus(surveyStatusNumber: number): string {
    return SurveyStatus[surveyStatusNumber];
  }

  public displayManger(selected: ProjectManagers): string {
    return (
      this.managers.find((m) => m.id === selected.id)?.name || 'Select one'
    );
  }

  public billingNumberEnabled() {
    return !!this._auth.buyerConfig?.enableBillingNumberRules;
  }

  private _fetchBillingNumberRules(): void {
    this.billingRules$ = this._billingRulesService.fetchRules(
      this._auth.user?.cmp || 0
    );
  }

  private _opearateIndividually(
    surveys: EditableSurvey[]
  ): Observable<EditableSurvey[]> {
    return from(surveys).pipe(
      mergeMap((state) => {
        if (this._isSurveysHaveError([state], true)) {
          return of(state);
        }
        const { survey, highCpiDetails } = state;
        const found = this._modalData.requests.find(
          (request) => request.survey() === survey.surveyId
        )!;
        const editable = this._surveys.find(
          ({ id }) => id === survey.surveyId
        )!;
        const request = found.update(editable.value);
        return this._operate(request, survey, highCpiDetails);
      }),
      toArray()
    );
  }

  private _operateCollectively(
    surveys: EditableSurvey[]
  ): Observable<EditableSurvey[]> {
    const data = this._modalData.requests[0];
    if (data.type() === 'group-surveys') {
      return this._groupSurveys(
        data as BulkGroupSurveysRequestConfig<BulkEditPayloadValue>
      );
    }

    return of(surveys);
  }

  private _operate(
    data: BulkRequestConfig<BulkEditPayloadValue>,
    survey: SurveyListing,
    highCpiDetails?: HighCpiConfrmationResponse
  ): Observable<EditableSurvey> {
    if (data.type() === 'delete-surveys') {
      return this._delete(
        data as BulkDeleteSurveyRequestConfig<BulkEditPayloadValue>,
        survey,
        highCpiDetails
      );
    }

    return this._update(
      data as BulkPatchSurveyRequestConfig<BulkEditPayloadValue>,
      survey,
      highCpiDetails
    );
  }

  private _update(
    data: BulkPatchSurveyRequestConfig<BulkEditPayloadValue>,
    survey: SurveyListing,
    highCpiDetails?: HighCpiConfrmationResponse
  ) {
    const observable = this._buyerApiService
      .patchSurvey(data.survey(), data.payload())
      .pipe(
        map((response) =>
          this.processSurveyResponse(response, survey, data, highCpiDetails)
        )
      );
    return this.processResponse(observable, data, survey, highCpiDetails);
  }

  private _delete(
    data: BulkDeleteSurveyRequestConfig<BulkEditPayloadValue>,
    survey: SurveyListing,
    highCpiDetails?: HighCpiConfrmationResponse
  ) {
    const observable = this._buyerApiService
      .deleteDraftSurvey(data.survey())
      .pipe(map(() => new EditableSurveyStateComposer(survey, data.value())));
    return this.processResponse(observable, data, survey, highCpiDetails);
  }

  private _groupSurveys(
    data: BulkGroupSurveysRequestConfig<BulkEditPayloadValue>
  ): Observable<EditableSurvey[]> {
    const updated = data.update({
      ...data.payload(),
      survey_ids: [...new Set(this._surveys.map((s) => s.id))],
    });
    return this._buyerApiService.groupSurveys(updated.payload()).pipe(
      switchMap(() =>
        this._handleSuccessResponseForBulkCollectiveOperation(data)
      ),
      catchError((error) =>
        this._handleErrorsForBulkCollectiveOperation(data, error)
      )
    );
  }

  private _handleSuccessResponseForBulkCollectiveOperation(
    data: BulkGroupSurveysRequestConfig<BulkEditPayloadValue>
  ): Observable<EditableSurveyEdited[]> {
    return from(this._surveys).pipe(
      map((state) =>
        new EditableSurveyStateComposer(state.survey, data.value()).edited()
      ),
      toArray()
    );
  }

  private _handleErrorsForBulkCollectiveOperation(
    data: BulkGroupSurveysRequestConfig<BulkEditPayloadValue>,
    error: any
  ): Observable<EditableSurveyError[]> {
    return from(this._surveys).pipe(
      filter((state) => {
        const surveysWithError: number[] =
          error?.error?.ps_api_response_data?.invalidSurveyIds;
        if (!surveysWithError || !surveysWithError.length) {
          return true;
        }

        return surveysWithError.includes(state.id);
      }),
      map((state) => {
        const response = new EditableSurveyStateComposer(
          state.survey,
          data.value()
        ).error(error?.error?.ps_api_response_message || error);
        this._feedbackCommand$.next(response);
        return response;
      }),
      toArray()
    );
  }

  public processResponse(
    observable: Observable<ComposedState>,
    data: BulkRequestConfig<BulkEditPayloadValue>,
    survey: SurveyListing,
    highCpiDetails?: HighCpiConfrmationResponse
  ) {
    return observable.pipe(
      map((state) => state.edited()),
      tap((response) => this._feedbackCommand$.next(response)),
      catchError((error) => {
        const response = new EditableSurveyStateComposer(
          survey,
          data.value(),
          highCpiDetails
        ).error(error?.error?.ps_api_response_message || error);
        this._feedbackCommand$.next(response);
        return of(response);
      })
    );
  }

  public onTableCellClicked(survey: SurveyListing): void {
    const route = new SurveyRouterLinkPipe().transform(survey);
    const url = this._route.serializeUrl(this._route.createUrlTree(route));
    window.open(url, '_blank');
  }

  public onValueChange(item: EditableSurvey, value: BulkEditPayloadValue) {
    const state = new EditableSurveyStateComposer(
      item.survey,
      value,
      item.highCpiDetails
    );
    const updatedSurvey = this.view.validate(state, 'value-change');
    this._feedbackCommand$.next(updatedSurvey);
  }

  public billingNumberOnAlredyInvoicedSurvey(
    billingCode: string
  ): Observable<String> {
    return this._buyerApiService.getSurveyAlredyInvoicedWithPONumber(
      billingCode
    );
  }

  public onBillingNumberChange(value: string): void {
    this.billingNumberOnAlredyInvoicedSurvey(value).subscribe({
      error: () => {
        this._toast.warning(
          notifyMessage.errorMessage.BULK_EDIT.BILLING_CODE_ALREADY_USED
        );
      },
    });
  }

  public onValueConfirmation(survey: EditableSurvey) {
    const state = new EditableSurveyStateComposer(
      survey.survey,
      survey.value,
      survey.highCpiDetails
    );
    const updatedSurvey: EditableSurvey = state.pending();
    this._feedbackCommand$.next(updatedSurvey);
    this._highCpiConfirmationActivity$.next(updatedSurvey);
  }

  public concatParentWithChilds(
    parent: SurveyListing,
    child: SurveyListing
  ): SurveyListing {
    const project = child.project;
    if (project) {
      return { ...parent, project };
    }
    return parent;
  }

  public handleProjectError(
    projectResponse: ProjectResponse[],
    survey: SurveyListing
  ): void {
    const matchingSurveys = this._getMatchingSurveys(projectResponse, survey);
    const combinedResponses = projectResponse.map((response) => {
      const matchingSurvey = matchingSurveys.find(
        (s) => s.surveyId === response.surveyId
      );
      return {
        ...response,
        survey: matchingSurvey,
        projectType: survey.project_type,
      };
    });

    const errorResponses = combinedResponses.filter(
      (response) => response.status === 'error'
    );

    const errorObject =
      this._errorMessageFormatterService.constructErrorMessage(errorResponses);

    throw new Error(JSON.stringify(errorObject));
  }

  public parseError(
    error: { message: string },
    survey: SurveyListing,
    needFormat = false
  ) {
    if (!needFormat) {
      return { message: error };
    }

    if (
      survey.project_type &&
      Object.values(PROJECT_TYPES).includes(survey.project_type) &&
      needFormat
    ) {
      try {
        return JSON.parse(error.message);
      } catch (e) {
        return false;
      }
    }
  }

  private _getMatchingSurveys(
    projectResponse: ProjectResponse[],
    survey: SurveyListing
  ): (McSVPEachSurvey | (ChildTrackerStats & { surveyId: number }))[] {
    const surveyIds = new Set(
      projectResponse.map((response) => response.surveyId)
    );
    switch (survey.project_type) {
      case PROJECT_TYPES.MCQB:
        return (
          survey?.mc?.surveys?.filter((s) => surveyIds.has(s.surveyId)) || []
        );
      case PROJECT_TYPES.TRACKER:
        return (
          survey?.tracker?.surveys?.filter((s) => surveyIds.has(s.surveyId)) ||
          []
        );
      default:
        return [];
    }
  }

  public processSurveyResponse(
    response: SurveyListing,
    survey: SurveyListing,
    data: BulkRequestConfig<BulkEditPayloadValue>,
    highCpiDetails?: HighCpiConfrmationResponse
  ): ComposedState {
    const updatedSurvey = this.concatParentWithChilds(survey, response);
    const projectsWithErrorResponse = updatedSurvey?.project?.filter(
      (s) => s.status === 'error'
    );

    if (projectsWithErrorResponse && projectsWithErrorResponse.length > 0) {
      this.handleProjectError(projectsWithErrorResponse, survey);
    }

    return new EditableSurveyStateComposer(
      updatedSurvey,
      data.value(),
      highCpiDetails
    ) as ComposedState;
  }

  private _handleDynamicColumns(
    surveys: EditableSurvey[]
  ): Observable<EditableSurvey[]> {
    return of(surveys).pipe(
      switchMap(() =>
        iif(
          () => this.view.operation() === 'Edit STATUS',
          this._handleBillingNumberColumnForBulkInvoice(surveys),
          of(true)
        )
      ),
      switchMap(() =>
        iif(
          () => this.view.operation() === 'Edit STATUS',
          this._handleHighCpiColumnOnStatusChange(surveys),
          of(true)
        )
      ),
      switchMap(() => this._handleInfoColumn(surveys)),
      map(() => surveys)
    );
  }

  private _handleInfoColumn(surveys: EditableSurvey[]): Observable<boolean> {
    return of(surveys).pipe(
      map(() =>
        surveys.some(
          (s) => s.status === 'error' && s.position === 'info-column'
        )
      ),
      tap((hasInfo) => {
        if (hasInfo) {
          this.view.addColumn(new BulkViewInfoColumn());
          return;
        }
        this.view.removeColumn(new BulkViewInfoColumn());
      })
    );
  }

  private _handleBillingNumberColumnForBulkInvoice(
    surveys: EditableSurvey[]
  ): Observable<boolean> {
    return of(surveys).pipe(
      map(() => surveys.some((s) => Number(s.value) === SURVEY_STATUS.INVOICE)),
      tap((hasEditableBillingColumn) => {
        // When it's invoice selection in any of the surveys, add billing column and remove cost column
        const editableBillingColumn = new BulkViewBillingActionColumn();
        const editableStatusColumn = new BulkViewStatusColumn(true);
        const costColumn = new BulkViewCostColumn();

        if (hasEditableBillingColumn) {
          this.view.addColumn(editableBillingColumn, editableStatusColumn);
          this.view.removeColumn(costColumn);
          return;
        }

        this.view.addColumn(costColumn, editableStatusColumn);
        this.view.removeColumn(editableBillingColumn);
      })
    );
  }

  private _handleHighCpiColumnOnStatusChange(surveys: EditableSurvey[]) {
    return of(surveys).pipe(
      map(() => surveys.filter((s) => Number(s.value) === SURVEY_STATUS.LIVE)),
      tap((surveysToBeSetLive) => {
        // When any survey's destination status is LIVE, then add High CPI column, otherwise remove it
        const highCpicolumn = new BulkViewHighCpiColumn();
        const currentCpiColumn = new BulkViewCurrencyColumn(
          'aCpi',
          'Current CPI'
        );

        if (surveysToBeSetLive.length) {
          this.view.addColumn(highCpicolumn, currentCpiColumn);
          return;
        }

        this.view.removeColumn(highCpicolumn);
      })
    );
  }

  public activateBillingState(
    item: EditableSurvey,
    billingState: 'view' | 'edit'
  ): void {
    const state = new EditableSurveyStateComposer(
      item.survey,
      item.value,
      item.highCpiDetails
    );
    const surveyMode: SurveyModePending = {
      billing_id: billingState,
    };
    const updatedSurvey: EditableSurvey = state.pending(surveyMode);
    this._feedbackCommand$.next(updatedSurvey);
  }

  public updateBillingNumberInSurvey(
    item: EditableSurvey,
    newBillingNum: string
  ): void {
    const state = new EditableSurveyStateComposer(
      item.survey,
      item.value,
      item.highCpiDetails
    );
    const surveyMode: SurveyModePending = {
      billing_id: 'view',
    };
    const updatedSurvey: EditableSurvey = state.pending(surveyMode);
    updatedSurvey.survey.billing_id = newBillingNum;
    this._feedbackCommand$.next(updatedSurvey);
  }

  public onBillingValueUpdate(
    item: EditableSurvey,
    newBillingNum: string
  ): void {
    const data = new BulkEditBillingNumberRequest(
      item.survey.surveyId,
      newBillingNum
    );
    this._buyerApiService
      .patchSurvey(data.survey(), data.payload())
      .pipe(tap(() => this.activateBillingState(item, 'view')))
      .subscribe({
        next: () => {
          this._toast.success(
            notifyMessage.successMessage.BILLING_NUMBER.UPDATED
          );
          this.updateBillingNumberInSurvey(item, newBillingNum);
        },
        error: (error) => {
          const errorMessage = error.error.ps_api_response_message;
          this._toast.error(errorMessage);
        },
      });
  }

  public isMarginEditable(item: EditableSurvey) {
    return (
      item.survey?.is_enable_margin_override &&
      !item.survey?.manual_scpi_override_enabled &&
      !item.survey.is_enable_margin_max
    );
  }
}
